import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";
import { getNewsById, getWorkById, relatedNews } from "@/mocks";

export default function NewsDetail() {
  const { id } = useParams();
  const article = getNewsById(id);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [id]);

  if (!article) {
    return (
      <EmptyState
        title="资讯内容不存在"
        description="这条资讯可能已被移除，或者链接地址有误。可返回资讯列表查看更多影视动态。"
        icon="ri-file-unknow-line"
        backTo="/news"
        backLabel="返回资讯列表"
      />
    );
  }

  const related = relatedNews(article, 3);
  const work = getWorkById(article.relatedWorkId);

  return (
    <SiteLayout>
      <article className="w-full bg-background-50">
        <div className="mx-auto w-full max-w-content px-4 pt-4 md:px-6 md:pt-6">
          <Breadcrumb
            items={[{ label: "首页", to: "/" }, { label: "影视资讯", to: "/news" }, { label: article.title }]}
          />
        </div>

        <header className="mx-auto w-full max-w-content px-4 pb-5 pt-4 md:px-6 md:pt-6">
          <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            <span className="rounded bg-accent-100 px-2 py-0.5 font-medium text-accent-900">{article.tag}</span>
            <span className="whitespace-nowrap">{article.date}</span>
            <span className="text-background-400">·</span>
            <span className="whitespace-nowrap">{article.author}</span>
          </div>
          <h1 className="mt-3 max-w-4xl font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-[32px]">
            {article.title}
          </h1>
          <p className="mt-3 max-w-3xl rounded-lg border-l-2 border-accent-400 bg-background-100 px-4 py-3 text-sm leading-relaxed text-foreground-700">
            {article.summary}
          </p>
        </header>

        <div className="mx-auto grid w-full max-w-content grid-cols-1 gap-8 px-4 pb-10 md:px-6 md:pb-14 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-8">
            <div className="space-y-4 rounded-lg border border-background-200/80 bg-background-50 p-4 md:p-6">
              {article.content.map((paragraph, i) => (
                <p key={i} className="text-sm leading-[1.95] text-foreground-700 md:text-[15px]">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-5 flex flex-col gap-3 rounded-lg bg-background-100 p-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs leading-relaxed text-foreground-600">
                本文为演示资讯内容，文中信息仅用于页面展示，不代表真实报道。
              </p>
              <Link
                to="/news"
                className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-xs font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-arrow-left-line text-base leading-none" />
                </span>
                返回资讯列表
              </Link>
            </div>
          </div>

          <aside className="lg:col-span-4">
            {work && (
              <div className="rounded-lg border border-background-200/80 bg-background-100 p-4">
                <h2 className="font-heading text-sm font-bold text-foreground-950">相关影视作品</h2>
                <Link to={`/detail/${work.id}`} className="group mt-3 flex cursor-pointer items-center gap-3">
                  <span className="h-20 w-14 shrink-0 overflow-hidden rounded bg-background-200">
                    <img
                      src={work.poster}
                      alt={`${work.title} 海报`}
                      title={`${work.title} 剧情介绍`}
                      className="h-full w-full object-cover object-top"
                    />
                  </span>
                  <span className="min-w-0">
                    <span className="line-clamp-2 block text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-700">
                      {work.title}
                    </span>
                    <span className="mt-1 block text-[11px] text-foreground-500">
                      {work.year} · {work.region} · {work.genres.join(" / ")}
                    </span>
                    <span className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-primary-600">
                      查看剧情详情
                      <i className="ri-arrow-right-line text-xs leading-none" />
                    </span>
                  </span>
                </Link>
              </div>
            )}

            <div className="mt-4 rounded-lg border border-background-200/80 bg-background-50 p-4">
              <h2 className="font-heading text-sm font-bold text-foreground-950">相关资讯推荐</h2>
              <div className="mt-1">
                {related.map((item) => (
                  <Link
                    key={item.id}
                    to={`/news/detail/${item.id}`}
                    className="group flex cursor-pointer items-start gap-2.5 border-b border-background-200/70 py-3 last:border-b-0"
                  >
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-background-100 text-primary-600">
                      <i className="ri-file-text-line text-xs leading-none" />
                    </span>
                    <span className="min-w-0">
                      <span className="line-clamp-2 block text-xs leading-relaxed text-foreground-900 transition-colors group-hover:text-primary-700">
                        {item.title}
                      </span>
                      <span className="mt-1 block text-[11px] text-foreground-500">{item.tag} · {item.date}</span>
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </article>
    </SiteLayout>
  );
}